import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home, Users, FolderKanban, DollarSign, User, Bell } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import "./SideMenu.css";

export default function ManagerSideMenu() {
  const location = useLocation();
  const navigate = useNavigate();
  const popupRef = useRef(null);
  const navigationIntent = useRef(null); // ← guard to avoid flicker

  const menuItems = [
    {
      id: "dashboard",
      icon: <Home size={22} />,
      label: "Dashboard",
      path: "/manager",
    },
    {
      id: "employees",
      icon: <Users size={22} />,
      label: "Employees",
      path: "/manager/employees",
    },
    {
      id: "clients",
      icon: <User size={22} />,
      label: "Clients",
      path: "/manager/clients",
    },
    {
      id: "projects",
      icon: <FolderKanban size={22} />,
      label: "Projects",
      path: "/manager/projects",
    },
    {
      id: "payments",
      icon: <DollarSign size={22} />,
      label: "Payments",
      path: "/manager/payments",
    },
    {
      id: "notifications",
      icon: <Bell size={22} className="amsm_icon_x1" />,
      label: "Notifications",
      path: "/manager/notifications",
    },
  ];

  function getActiveId(pathname) {
    const all = [
      ...menuItems,
      { id: "projects-main", path: "/manager/projects" },
      { id: "milestones", path: "/manager/milestones" },
      { id: "tasks", path: "/manager/tasks" },
      { id: "assigntasks", path: "/manager/assigntasks" },
    ];
    const found = all.find(
      (i) => pathname === i.path || pathname.startsWith(i.path + "/")
    );
    return found ? found.id : "";
  }

  const [active, setActive] = useState(() => getActiveId(location.pathname));
  const [popupOpen, setPopupOpen] = useState(false);

  // Sync with URL, but respect navigationIntent to avoid flicker
  useEffect(() => {
    const derived = getActiveId(location.pathname);

    // keep popupOpen synced always
    setPopupOpen(
      location.pathname.startsWith("/manager/projects") ||
        location.pathname.startsWith("/manager/milestones") ||
        location.pathname.startsWith("/manager/tasks") ||
        location.pathname.startsWith("/manager/assigntasks")
    );

    // If we initiated navigation, do not immediately override active.
    // If the derived matches the intent, clear the intent and set active to derived (final sync).
    if (navigationIntent.current) {
      if (navigationIntent.current === derived) {
        navigationIntent.current = null;
        setActive(derived);
      }
      return; // skip overriding active while intent exists
    }

    setActive(derived);
  }, [location.pathname]);

  // Click outside closes popup
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setPopupOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const togglePopup = () => {
    setPopupOpen((prev) => {
      const next = !prev;
      if (next) setActive("projects"); // keep projects visually active when opened
      return next;
    });
  };

  // Use navigationIntent to avoid the URL-sync effect from stealing the indicator
  const goTo = (path, id) => {
    if (id) {
      navigationIntent.current = id;
      setActive(id); // immediate visual response
    }
    // navigate (this will update location and trigger the above effect later)
    navigate(path);

    // safety: clear intent after short time if something weird happens
    window.setTimeout(() => {
      if (navigationIntent.current === id) navigationIntent.current = null;
    }, 400);
  };

  return (
    <div className="side-menu" ref={popupRef}>
      {menuItems.map((item) => (
        <div key={item.id} className="menu-item-wrapper">
          <motion.button
            onClick={() =>
              item.id === "projects" ? togglePopup() : goTo(item.path, item.id)
            }
            className={`menu-button ${active === item.id ? "active" : ""}`}
            aria-label={item.label}
          >
            {item.icon}
            {active === item.id && (
              <motion.span
                layoutId="activeIndicator"
                className="active-indicator"
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
              />
            )}
          </motion.button>

          {item.id === "projects" && (
            <AnimatePresence>
              {popupOpen && (
                <motion.div
                  key="projects-popup"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: 0.18 }}
                  className="popup-menu"
                >
                  {[
                    {
                      id: "projects-main",
                      label: "Manage Projects",
                      path: "/manager/projects",
                    },
                    {
                      id: "milestones",
                      label: "Manage Milestones",
                      path: "/manager/milestones",
                    },
                    {
                      id: "tasks",
                      label: "Manage Tasks",
                      path: "/manager/tasks",
                    },
                    {
                      id: "assigntasks",
                      label: "Assign Tasks",
                      path: "/manager/assigntasks",
                    },
                  ].map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => goTo(sub.path, sub.id)}
                      className={`popup-item ${
                        active === sub.id ? "active" : ""
                      }`}
                    >
                      {sub.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
      ))}
    </div>
  );
}
